import { useRef } from 'react'
import type { PainLocation } from '@yui-receipt/shared'

type Props = {
  locations: PainLocation[]
  onChange: (locs: PainLocation[]) => void
  disabled?: boolean
}

const BODY_W = 160
const BODY_H = 340
const DOT_R = 6

export default function BodyDiagram({ locations, onChange, disabled }: Props) {
  const frontRef = useRef<SVGSVGElement>(null)
  const backRef = useRef<SVGSVGElement>(null)

  function handleClick(side: 'front' | 'back', e: React.MouseEvent<SVGSVGElement>) {
    if (disabled) return
    const svg = side === 'front' ? frontRef.current : backRef.current
    if (!svg) return
    const rect = svg.getBoundingClientRect()
    const x = Math.round(((e.clientX - rect.left) / rect.width) * BODY_W)
    const y = Math.round(((e.clientY - rect.top) / rect.height) * BODY_H)
    onChange([...locations, { x, y, side }])
  }

  function removeDot(idx: number) {
    if (disabled) return
    onChange(locations.filter((_, i) => i !== idx))
  }

  const frontDots = locations.filter((l) => l.side === 'front')
  const backDots = locations.filter((l) => l.side === 'back')

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <label className="label mb-0">ตำแหน่งที่ปวด (กดบนรูป)</label>
        {locations.length > 0 && (
          <button
            type="button"
            className="text-xs text-rose-600 hover:underline"
            onClick={() => onChange([])}
            disabled={disabled}
          >
            ลบทั้งหมด
          </button>
        )}
      </div>
      <div className="flex gap-4 justify-center">
        {/* Front */}
        <div className="text-center">
          <div className="text-xs text-slate-500 mb-1">ด้านหน้า</div>
          <svg
            ref={frontRef}
            viewBox={`0 0 ${BODY_W} ${BODY_H}`}
            className="w-[120px] sm:w-[140px] h-auto border border-slate-200 rounded-lg bg-white cursor-crosshair"
            onClick={(e) => handleClick('front', e)}
          >
            <FrontBody />
            {frontDots.map((loc) => {
              const idx = locations.indexOf(loc)
              return (
                <circle
                  key={idx}
                  cx={loc.x}
                  cy={loc.y}
                  r={DOT_R}
                  fill="rgba(239, 68, 68, 0.7)"
                  stroke="#dc2626"
                  strokeWidth={1.5}
                  className="cursor-pointer"
                  onClick={(e) => {
                    e.stopPropagation()
                    removeDot(idx)
                  }}
                />
              )
            })}
          </svg>
        </div>
        {/* Back */}
        <div className="text-center">
          <div className="text-xs text-slate-500 mb-1">ด้านหลัง</div>
          <svg
            ref={backRef}
            viewBox={`0 0 ${BODY_W} ${BODY_H}`}
            className="w-[120px] sm:w-[140px] h-auto border border-slate-200 rounded-lg bg-white cursor-crosshair"
            onClick={(e) => handleClick('back', e)}
          >
            <BackBody />
            {backDots.map((loc) => {
              const idx = locations.indexOf(loc)
              return (
                <circle
                  key={idx}
                  cx={loc.x}
                  cy={loc.y}
                  r={DOT_R}
                  fill="rgba(239, 68, 68, 0.7)"
                  stroke="#dc2626"
                  strokeWidth={1.5}
                  className="cursor-pointer"
                  onClick={(e) => {
                    e.stopPropagation()
                    removeDot(idx)
                  }}
                />
              )
            })}
          </svg>
        </div>
      </div>
      {locations.length > 0 && (
        <p className="text-xs text-slate-500 text-center">
          {locations.length} จุด — กดจุดแดงเพื่อลบ
        </p>
      )}
    </div>
  )
}

function FrontBody() {
  return (
    <g stroke="#94a3b8" strokeWidth={1.2} fill="none" strokeLinecap="round" strokeLinejoin="round">
      {/* Head */}
      <ellipse cx={80} cy={28} rx={18} ry={22} />
      {/* Neck */}
      <line x1={74} y1={50} x2={74} y2={62} />
      <line x1={86} y1={50} x2={86} y2={62} />
      {/* Torso */}
      <path d="M 54 62 L 54 170 Q 54 180 64 180 L 96 180 Q 106 180 106 170 L 106 62 Z" />
      {/* Shoulders */}
      <line x1={54} y1={65} x2={30} y2={75} />
      <line x1={106} y1={65} x2={130} y2={75} />
      {/* Left Arm */}
      <path d="M 30 75 L 22 140 L 18 180 L 14 210" />
      <path d="M 38 75 L 30 140 L 26 180 L 22 210" />
      {/* Right Arm */}
      <path d="M 130 75 L 138 140 L 142 180 L 146 210" />
      <path d="M 122 75 L 130 140 L 134 180 L 138 210" />
      {/* Waist line */}
      <line x1={54} y1={130} x2={106} y2={130} strokeDasharray="4 3" />
      {/* Left Leg */}
      <path d="M 54 180 L 48 240 L 44 290 L 42 330" />
      <path d="M 74 180 L 68 240 L 64 290 L 62 330" />
      {/* Right Leg */}
      <path d="M 86 180 L 92 240 L 96 290 L 98 330" />
      <path d="M 106 180 L 112 240 L 116 290 L 118 330" />
      {/* Feet */}
      <path d="M 42 330 Q 40 336 48 336 L 62 336 Q 66 336 64 330" />
      <path d="M 98 330 Q 96 336 104 336 L 118 336 Q 122 336 120 330" />
    </g>
  )
}

function BackBody() {
  return (
    <g stroke="#94a3b8" strokeWidth={1.2} fill="none" strokeLinecap="round" strokeLinejoin="round">
      {/* Head */}
      <ellipse cx={80} cy={28} rx={18} ry={22} />
      {/* Neck */}
      <line x1={74} y1={50} x2={74} y2={62} />
      <line x1={86} y1={50} x2={86} y2={62} />
      {/* Torso */}
      <path d="M 54 62 L 54 170 Q 54 180 64 180 L 96 180 Q 106 180 106 170 L 106 62 Z" />
      {/* Spine */}
      <line x1={80} y1={62} x2={80} y2={175} strokeDasharray="4 3" />
      {/* Shoulders */}
      <line x1={54} y1={65} x2={30} y2={75} />
      <line x1={106} y1={65} x2={130} y2={75} />
      {/* Left Arm */}
      <path d="M 30 75 L 22 140 L 18 180 L 14 210" />
      <path d="M 38 75 L 30 140 L 26 180 L 22 210" />
      {/* Right Arm */}
      <path d="M 130 75 L 138 140 L 142 180 L 146 210" />
      <path d="M 122 75 L 130 140 L 134 180 L 138 210" />
      {/* Shoulder blades */}
      <ellipse cx={66} cy={90} rx={8} ry={14} strokeDasharray="3 3" />
      <ellipse cx={94} cy={90} rx={8} ry={14} strokeDasharray="3 3" />
      {/* Left Leg */}
      <path d="M 54 180 L 48 240 L 44 290 L 42 330" />
      <path d="M 74 180 L 68 240 L 64 290 L 62 330" />
      {/* Right Leg */}
      <path d="M 86 180 L 92 240 L 96 290 L 98 330" />
      <path d="M 106 180 L 112 240 L 116 290 L 118 330" />
      {/* Feet */}
      <path d="M 42 330 Q 40 336 48 336 L 62 336 Q 66 336 64 330" />
      <path d="M 98 330 Q 96 336 104 336 L 118 336 Q 122 336 120 330" />
    </g>
  )
}
